import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Modal,
  Row,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  LockOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import Globais from "../../../components/Globais";

const { Title, Text, Paragraph, Link } = Typography;

type Props = {
  loading?: boolean;
  taxa: number;
  prazoBuscaDiasUteis?: number;
  prazoPagamentoRestanteDias?: number;
  initialValues?: {
    aceitouTermosUso?: boolean;
    aceitouPoliticaPrivacidade?: boolean;
    aceitouTermoTaxa?: boolean;
  };
  onSubmit: (payload: {
    aceitouTermosUso: boolean;
    aceitouPoliticaPrivacidade: boolean;
    aceitouTermoTaxa: boolean;
  }) => Promise<void> | void;
};

export default function StepTaxaTermosPagamento({
  loading = false,
  taxa,
  prazoBuscaDiasUteis = 7,
  initialValues = {},
  prazoPagamentoRestanteDias = 3,
  onSubmit,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [aceitouTermosUso, setAceitouTermosUso] = useState(
    initialValues?.aceitouTermosUso ?? false
  );
  const [aceitouPoliticaPrivacidade, setAceitouPoliticaPrivacidade] = useState(
    initialValues?.aceitouPoliticaPrivacidade ?? false
  );
  const [aceitouTermoTaxa, setAceitouTermoTaxa] = useState(
    initialValues?.aceitouTermoTaxa ?? false
  );

  const podePagar = useMemo(
    () => aceitouTermosUso && aceitouPoliticaPrivacidade && aceitouTermoTaxa,
    [aceitouPoliticaPrivacidade, aceitouTermoTaxa, aceitouTermosUso]
  );

  const moeda = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const handleSubmit = async () => {
    if (!podePagar) return;

    await onSubmit({
      aceitouTermosUso,
      aceitouPoliticaPrivacidade,
      aceitouTermoTaxa,
    });
  };

  return (
    <>
      <Card
        bordered={false}
        style={{
          borderRadius: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
        styles={{ body: { padding: 0 } }}
      >
        <div
          style={{
            padding: 24,
            background:
              "linear-gradient(135deg, rgba(22,119,255,0.10) 0%, rgba(22,119,255,0.03) 100%)",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <Tag
              icon={<LockOutlined />}
              color="blue"
              style={{
                borderRadius: 999,
                width: "fit-content",
                paddingInline: 10,
                margin: 0,
              }}
            >
              Etapa final antes do checkout
            </Tag>

            <div>
              <Title level={2} style={{ margin: 0 }}>
                Confirmação da taxa de verificação
              </Title>
              <Text type="secondary" style={{ fontSize: 16 }}>
                Revise as condições abaixo e confirme seu aceite para seguir para o
                checkout da taxa.
              </Text>
            </div>
          </Space>
        </div>

        <div style={{ padding: 24 }}>
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <Card
              bordered={false}
              style={{
                borderRadius: 18,
                background: "linear-gradient(135deg, #ffffff 0%, #fafcff 100%)",
                border: "1px solid rgba(22,119,255,0.12)",
              }}
              styles={{ body: { padding: 20 } }}
            >
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={16}>
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <Text type="secondary" style={{ fontSize: 13, letterSpacing: 0.3 }}>
                      TAXA DE VERIFICAÇÃO
                    </Text>

                    <Title level={2} style={{ margin: 0, lineHeight: 1.1 }}>
                      {moeda(taxa)}
                    </Title>

                    <Text style={{ fontSize: 15 }}>
                      O pagamento dessa taxa inicia a busca de disponibilidade e a
                      intermediação da sua solicitação.
                    </Text>

                    <Space direction="vertical" size={4}>
                      <Text>
                        <CheckCircleOutlined style={{ marginRight: 8 }} />
                        Será abatida do valor total da hospedagem
                      </Text>
                      <Text>
                        <CheckCircleOutlined style={{ marginRight: 8 }} />
                        Inicia o processo de verificação da reserva
                      </Text>
                      <Text>
                        <CheckCircleOutlined style={{ marginRight: 8 }} />
                        Após o início do procedimento, não é reembolsável
                      </Text>
                      <Text>
                        <CreditCardOutlined style={{ marginRight: 8 }} />
                        No próximo passo você poderá escolher Pix ou cartão no checkout
                      </Text>
                    </Space>
                  </Space>
                </Col>

                <Col xs={24} md={8}>
                  <Alert
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    message="Importante"
                    description="O pagamento da taxa não representa confirmação imediata da reserva."
                    style={{ borderRadius: 14 }}
                  />
                </Col>
              </Row>

              <div style={{ marginTop: 14 }}>
                <Button
                  type="link"
                  style={{ padding: 0, fontWeight: 600 }}
                  onClick={() => setModalOpen(true)}
                >
                  Entenda como funciona
                </Button>
              </div>
            </Card>

            <Card
              bordered={false}
              style={{
                borderRadius: 18,
                background: "rgba(0,0,0,0.02)",
              }}
              styles={{ body: { padding: 20 } }}
            >
              <Space direction="vertical" size={14} style={{ width: "100%" }}>
                <Text strong style={{ fontSize: 16 }}>
                  Prazos do processo
                </Text>

                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <ClockCircleOutlined style={{ marginTop: 4 }} />
                  <div>
                    <Text strong>Verificação da reserva</Text>
                    <div>
                      <Text type="secondary">
                        Até {prazoBuscaDiasUteis} dias úteis para análise da disponibilidade
                      </Text>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <CreditCardOutlined style={{ marginTop: 4 }} />
                  <div>
                    <Text strong>Pagamento restante</Text>
                    <div>
                      <Text type="secondary">
                        Após a confirmação, você terá {prazoPagamentoRestanteDias} dias
                        para pagar o saldo
                      </Text>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <CheckCircleOutlined style={{ marginTop: 4 }} />
                  <div>
                    <Text strong>Abatimento da taxa</Text>
                    <div>
                      <Text type="secondary">
                        O valor pago agora será abatido do valor total da hospedagem
                      </Text>
                    </div>
                  </div>
                </div>
              </Space>
            </Card>

            <Card
              bordered={false}
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.06)",
              }}
              styles={{ body: { padding: 20 } }}
            >
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <div>
                  <Text strong style={{ fontSize: 16 }}>
                    Confirmações obrigatórias
                  </Text>
                  <div>
                    <Text type="secondary">
                      Para seguir, é necessário concordar com os itens abaixo.
                    </Text>
                  </div>
                </div>

                <Checkbox
                  checked={aceitouTermosUso}
                  onChange={(e) => setAceitouTermosUso(e.target.checked)}
                  disabled={loading}
                >
                  <Space size={6} wrap>
                    <FileTextOutlined />
                    <span>
                      Li e concordo com os{" "}
                      <Link href={`${Globais.DOMINIOFRONTPROP}/termodeuso`} target="_blank">
                        Termos de Uso
                      </Link>
                    </span>
                  </Space>
                </Checkbox>

                <Checkbox
                  checked={aceitouPoliticaPrivacidade}
                  onChange={(e) => setAceitouPoliticaPrivacidade(e.target.checked)}
                  disabled={loading}
                >
                  <Space size={6} wrap>
                    <SafetyOutlined />
                    <span>
                      Li e concordo com a{" "}
                      <Link
                        href={`${Globais.DOMINIOFRONTPROP}/politicaprivacidade`}
                        target="_blank"
                      >
                        Política de Privacidade
                      </Link>
                    </span>
                  </Space>
                </Checkbox>

                <Checkbox
                  checked={aceitouTermoTaxa}
                  onChange={(e) => setAceitouTermoTaxa(e.target.checked)}
                  disabled={loading}
                >
                  Entendo que a taxa de verificação inicia o processo de reserva e não
                  será devolvida após o início do procedimento
                </Checkbox>

                <Divider style={{ margin: "4px 0" }} />

                <Button
                  type="primary"
                  size="large"
                  block
                  loading={loading}
                  disabled={!podePagar}
                  onClick={handleSubmit}
                  style={{
                    height: 48,
                    borderRadius: 14,
                    fontWeight: 700,
                    fontSize: 16,
                  }}
                >
                  Pagar taxa
                </Button>

                <Text type="secondary" style={{ fontSize: 13 }}>
                  No próximo passo você poderá escolher Pix ou cartão no checkout.
                </Text>

                {!podePagar && (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Marque todos os campos obrigatórios para liberar o pagamento.
                  </Text>
                )}
              </Space>
            </Card>
          </Space>
        </div>
      </Card>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setModalOpen(false)}>
            Entendi
          </Button>,
        ]}
        title="Taxa de Verificação e Prioridade da Solicitação"
      >
        <Typography>
          <Paragraph>
            Para dar início ao processo de busca e intermediação da reserva, é cobrada
            uma <strong>Taxa de Verificação</strong> no valor de{" "}
            <strong>{moeda(taxa)}</strong>.
          </Paragraph>

          <Paragraph>Essa taxa tem como finalidade:</Paragraph>

          <ul>
            <li>Acionar o sistema de busca de proprietários compatíveis;</li>
            <li>Realizar contato e consulta de disponibilidade;</li>
            <li>Intermediar negociações de valor, quando necessário;</li>
            <li>Priorizar operacionalmente a sua solicitação dentro do prazo informado.</li>
          </ul>

          <Title level={5}>Regras da taxa</Title>

          <Paragraph>
            Após o início do procedimento de verificação e intermediação, o valor da taxa
            não é reembolsável em caso de desistência do interessado.
          </Paragraph>

          <Paragraph>
            Em caso de confirmação da reserva, o valor da taxa será abatido do valor total
            da hospedagem.
          </Paragraph>

          <Paragraph>
            No checkout, o cliente poderá escolher entre Pix e cartão.
          </Paragraph>

          <Paragraph>
            O pagamento da taxa implica concordância com essas condições.
          </Paragraph>
        </Typography>
      </Modal>
    </>
  );
}
